import React, { useState } from 'react';
import { IonIcon, IonSearchbar } from '@ionic/react';
import { addOutline, closeOutline, personAddOutline, personOutline, peopleOutline, trashOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { GamePlayer, GameSetup } from './types';

// Mock data - replace with actual API call
const MOCK_FRIENDS: GamePlayer[] = [
  { id: '1', name: 'John Smith', handicap: 12 },
  { id: '2', name: 'Sarah Johnson', handicap: 18 },
  { id: '3', name: 'Mike Williams', handicap: 8 },
  { id: '4', name: 'Emma Davis', handicap: 24 },
  { id: '5', name: 'Robert Brown', handicap: 6 },
  { id: '6', name: 'Lisa Wilson', handicap: 15 },
];

interface AddPlayersProps {
  initialData: Partial<GameSetup>;
  onNext: (data: Partial<GameSetup>) => void;
  onBack: () => void;
}

const AddPlayers: React.FC<AddPlayersProps> = ({ initialData, onNext, onBack }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddTemp, setShowAddTemp] = useState(false);
  const [tempPlayerName, setTempPlayerName] = useState('');
  const [tempPlayerHandicap, setTempPlayerHandicap] = useState('');
  const [selectedPlayers, setSelectedPlayers] = useState<GamePlayer[]>(
    initialData.players || []
  );

  const filteredFriends = MOCK_FRIENDS.filter(
    friend =>
      friend.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !selectedPlayers.some(player => player.id === friend.id)
  );

  const handleAddPlayer = (player: GamePlayer) => {
    setSelectedPlayers([...selectedPlayers, player]);
  };

  const handleRemovePlayer = (playerId: string) => {
    setSelectedPlayers(selectedPlayers.filter(player => player.id !== playerId));
  };

  const handleAddTempPlayer = () => {
    if (tempPlayerName.trim()) {
      const newPlayer: GamePlayer = {
        id: `temp-${Date.now()}`,
        name: tempPlayerName.trim(),
        handicap: tempPlayerHandicap ? parseInt(tempPlayerHandicap) : undefined,
        isTemporary: true,
      };
      setSelectedPlayers([...selectedPlayers, newPlayer]);
      setTempPlayerName('');
      setTempPlayerHandicap('');
      setShowAddTemp(false);
    }
  };

  const handleSubmit = () => {
    onNext({
      ...initialData,
      players: selectedPlayers,
    });
  };

  return (
    <div className="flex flex-col h-full p-4">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-4"
      >
        <h2 className="text-xl font-semibold">{t('Add Players')}</h2>
        <p className="text-muted-foreground text-sm">
          {t('Select players for your game')}
        </p>
      </motion.div>

      <div className="mb-4">
        <IonSearchbar
          value={searchQuery}
          onIonChange={(e) => setSearchQuery(e.detail.value || '')}
          placeholder={t('Search friends...')}
          className="p-0 bg-transparent"
        />
      </div>

      <div className="flex-1 overflow-auto">
        <div className="space-y-4">
          {/* Selected Players */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center">
              <IonIcon icon={peopleOutline} className="mr-1" />
              {t('Selected Players')} ({selectedPlayers.length})
            </h3>

            <AnimatePresence>
              {selectedPlayers.length > 0 ? (
                <motion.div className="space-y-2">
                  {selectedPlayers.map((player) => (
                    <motion.div
                      key={player.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3">
                          <IonIcon icon={personOutline} className="text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <div className="font-medium">{player.name}</div>
                          {player.handicap !== undefined && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {t('Handicap')}: {player.handicap}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemovePlayer(player.id)}
                        className="p-2 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
                      >
                        <IonIcon icon={trashOutline} className="size-5" />
                      </button>
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-4 text-center text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700"
                >
                  <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-2">
                    <IonIcon icon={peopleOutline} className="size-6 text-gray-400 dark:text-gray-500" />
                  </div>
                  <p>{t('No players added yet')}</p>
                  <p className="text-sm">{t('Add players to continue')}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Available Friends */}
          {searchQuery && filteredFriends.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                {t('Search Results')}
              </h3>
              <div className="space-y-2">
                {filteredFriends.map((friend) => (
                  <motion.div
                    key={friend.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mr-3">
                        <IonIcon icon={personOutline} className="text-gray-500 dark:text-gray-400" />
                      </div>
                      <div>
                        <div className="font-medium">{friend.name}</div>
                        {friend.handicap !== undefined && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {t('Handicap')}: {friend.handicap}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleAddPlayer(friend)}
                      className="p-2 text-green-500 hover:text-green-600 dark:text-green-400 dark:hover:text-green-300 transition-colors"
                    >
                      <IonIcon icon={addOutline} className="size-5" />
                    </button>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Add Temporary Player Form */}
          <AnimatePresence>
            {showAddTemp && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-blue-800 dark:text-blue-300">
                      {t('Add Temporary Player')}
                    </h3>
                    <button
                      onClick={() => setShowAddTemp(false)}
                      className="p-1 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <IonIcon icon={closeOutline} className="size-5" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label htmlFor="tempName" className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1 block">
                        {t('Player Name')}*
                      </label>
                      <input
                        id="tempName"
                        type="text"
                        value={tempPlayerName}
                        onChange={(e) => setTempPlayerName(e.target.value)}
                        placeholder={t('Enter player name')}
                        className="w-full p-2 border border-blue-300 dark:border-blue-700 rounded-lg bg-white dark:bg-gray-800"
                      />
                    </div>

                    <div>
                      <label htmlFor="tempHandicap" className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1 block">
                        {t('Handicap')} ({t('optional')})
                      </label>
                      <input
                        id="tempHandicap"
                        type="number"
                        value={tempPlayerHandicap}
                        onChange={(e) => setTempPlayerHandicap(e.target.value)}
                        placeholder={t('Enter handicap')}
                        className="w-full p-2 border border-blue-300 dark:border-blue-700 rounded-lg bg-white dark:bg-gray-800"
                      />
                    </div>

                    <button
                      onClick={handleAddTempPlayer}
                      disabled={!tempPlayerName.trim()}
                      className={`w-full py-2 px-4 rounded-lg font-medium flex items-center justify-center space-x-2 ${tempPlayerName.trim()
                        ? 'bg-blue-500 hover:bg-blue-600 text-white'
                        : 'bg-blue-300 text-blue-100 cursor-not-allowed'
                        }`}
                    >
                      <IonIcon icon={personAddOutline} className="size-5" />
                      <span>{t('Add Player')}</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Add Temporary Player Button */}
          {!showAddTemp && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onClick={() => setShowAddTemp(true)}
              className="w-full p-3 rounded-lg border border-dashed border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 flex items-center justify-center space-x-2 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
            >
              <IonIcon icon={personAddOutline} className="size-5" />
              <span>{t('Add Temporary Player')}</span>
            </motion.button>
          )}
        </div>
      </div>

      <div className="mt-auto pt-4 grid grid-cols-2 gap-3">
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          onClick={onBack}
          className="py-3 px-4 rounded-xl font-medium bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        >
          {t('Back')}
        </motion.button>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          onClick={handleSubmit}
          disabled={selectedPlayers.length === 0}
          className={`py-3 px-4 rounded-xl font-medium transition-colors ${selectedPlayers.length > 0
            ? 'bg-green-500 hover:bg-green-600 text-white'
            : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
        >
          {t('Continue')}
        </motion.button>
      </div>
    </div>
  );
};

export default AddPlayers;
